import React from 'react';
import { 
  Shield, 
  ShieldCheck, 
  Radio, 
  Smartphone, 
  Car, 
  Truck, 
  Users, 
  UserCheck, 
  Hammer, 
  Wrench, 
  Target, 
  Crosshair, 
  Package, 
  Box,
  Heart,
  Activity,
  FileText,
  Clipboard,
  HardHat,
  Eye,
  Camera,
  Lock,
  Key,
  Zap,
  Settings
} from 'lucide-react';

// Icon mapping for law enforcement categories
export const CATEGORY_ICONS = {
  'shield': { icon: Shield, label: 'Protective Gear', category: 'protection' },
  'shield-check': { icon: ShieldCheck, label: 'Body Armor', category: 'protection' },
  'radio': { icon: Radio, label: 'Radio Equipment', category: 'communications' },
  'smartphone': { icon: Smartphone, label: 'Mobile Devices', category: 'communications' },
  'car': { icon: Car, label: 'Patrol Vehicles', category: 'vehicles' },
  'truck': { icon: Truck, label: 'Specialty Vehicles', category: 'vehicles' },
  'users': { icon: Users, label: 'Personnel/Uniforms', category: 'personnel' },
  'user-check': { icon: UserCheck, label: 'Identification', category: 'personnel' },
  'hammer': { icon: Hammer, label: 'General Tools', category: 'equipment' },
  'wrench': { icon: Wrench, label: 'Maintenance Equipment', category: 'equipment' },
  'target': { icon: Target, label: 'Firearms', category: 'weapons' },
  'crosshair': { icon: Crosshair, label: 'Training Equipment', category: 'weapons' },
  'package': { icon: Package, label: 'General Equipment', category: 'general' },
  'box': { icon: Box, label: 'Storage/Supplies', category: 'general' },
  'heart': { icon: Heart, label: 'Medical Equipment', category: 'medical' },
  'activity': { icon: Activity, label: 'Health Monitoring', category: 'medical' },
  'file-text': { icon: FileText, label: 'Documentation', category: 'office' },
  'clipboard': { icon: Clipboard, label: 'Forms/Reports', category: 'office' },
  'hard-hat': { icon: HardHat, label: 'Safety Equipment', category: 'safety' },
  'eye': { icon: Eye, label: 'Surveillance', category: 'surveillance' },
  'camera': { icon: Camera, label: 'Photography', category: 'surveillance' },
  'lock': { icon: Lock, label: 'Security Equipment', category: 'security' },
  'key': { icon: Key, label: 'Access Control', category: 'security' },
  'zap': { icon: Zap, label: 'Electronic Equipment', category: 'electronics' },
  'settings': { icon: Settings, label: 'Technical Equipment', category: 'electronics' }
};

export type IconKey = keyof typeof CATEGORY_ICONS;

interface IconSelectorProps {
  value: string;
  onChange: (iconKey: string) => void;
  disabled?: boolean;
  className?: string;
}

const IconSelector: React.FC<IconSelectorProps> = ({
  value,
  onChange,
  disabled = false,
  className = ""
}) => {
  const selectedIcon = CATEGORY_ICONS[value as IconKey];
  const SelectedIconComponent = selectedIcon?.icon || Package;

  return (
    <div className={`relative ${className}`}>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white"
      >
        <option value="">Select an icon...</option>
        
        {/* Group icons by category */}
        <optgroup label="Protection & Safety">
          <option value="shield">Protective Gear</option>
          <option value="shield-check">Body Armor</option>
          <option value="hard-hat">Safety Equipment</option>
        </optgroup>
        
        <optgroup label="Weapons & Training">
          <option value="target">Firearms</option>
          <option value="crosshair">Training Equipment</option>
        </optgroup>
        
        <optgroup label="Communications">
          <option value="radio">Radio Equipment</option>
          <option value="smartphone">Mobile Devices</option>
        </optgroup>
        
        <optgroup label="Vehicles">
          <option value="car">Patrol Vehicles</option>
          <option value="truck">Specialty Vehicles</option>
        </optgroup>
        
        <optgroup label="Personnel">
          <option value="users">Personnel/Uniforms</option>
          <option value="user-check">Identification</option>
        </optgroup>
        
        <optgroup label="Equipment & Tools">
          <option value="hammer">General Tools</option>
          <option value="wrench">Maintenance Equipment</option>
          <option value="settings">Technical Equipment</option>
        </optgroup>
        
        <optgroup label="Medical">
          <option value="heart">Medical Equipment</option>
          <option value="activity">Health Monitoring</option>
        </optgroup>
        
        <optgroup label="Security & Surveillance">
          <option value="lock">Security Equipment</option>
          <option value="key">Access Control</option>
          <option value="eye">Surveillance</option>
          <option value="camera">Photography</option>
        </optgroup>
        
        <optgroup label="Electronics">
          <option value="zap">Electronic Equipment</option>
        </optgroup>
        
        <optgroup label="Office & Documentation">
          <option value="file-text">Documentation</option>
          <option value="clipboard">Forms/Reports</option>
        </optgroup>
        
        <optgroup label="General">
          <option value="package">General Equipment</option>
          <option value="box">Storage/Supplies</option>
        </optgroup>
      </select>
      
      {/* Icon preview */}
      <div className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
        <SelectedIconComponent className="h-4 w-4 text-gray-500" />
      </div>
    </div>
  );
};

// Helper function to get icon component by key
export const getIconComponent = (iconKey: string): React.ComponentType<any> => {
  return CATEGORY_ICONS[iconKey as IconKey]?.icon || Package;
};

// Helper function to render icon with consistent styling
export const renderCategoryIcon = (iconKey: string, className: string = "h-5 w-5") => {
  const IconComponent = getIconComponent(iconKey);
  return <IconComponent className={className} />;
};

export default IconSelector;